import React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { getColor, Theme } from '../theme/colors'

const error = (theme: Theme) => {
  return css`
    &,
    &:focus {
      caret-color: ${theme.colors.error};
      border-color: ${theme.colors.error};
    }
  `
}

const BaseInput = styled.input<{ invalid?: boolean; highlight?: boolean }>`
  appearance: none;
  min-height: 64px;
  display: block;
  align-items: center;
  padding: 0 1rem;
  font-family: inherit;
  border-radius: 1rem;
  letter-spacing: 0.01em;
  box-sizing: border-box;
  ${props => props.theme.typography.inputText};
  caret-color: ${getColor('inputFocusBorder')};
  border: 2px solid ${getColor('formFieldBackground')};
  background-color: ${getColor('formFieldBackground')};
  width: 100%;

  :focus {
    border: 2px solid ${getColor('inputFocusBorder')};
    background: ${getColor('background')};
  }

  &::placeholder {
    color: ${getColor('placeholderLabel')};
  }

  color: ${getColor('label')};

  &:focus {
    outline: none;
  }

  &:disabled {
    color: ${getColor('disabledLabel')};
    background: ${getColor('formFieldBackgroundDisabled')};
    border-color: ${getColor('formFieldBackgroundDisabled')};
  }

  ${({ invalid, theme }) => invalid && error(theme)}

  &.error {
    ${props => error(props.theme)}
  }
  ${props =>
    props.highlight &&
    css`
      background-color: ${props.theme.colors.bannerBackgroundSuccess};
      border-color: ${props.theme.colors.inputFocusBorder};
    `}

  /* Override default browser styling for auto filled inputs */
  &:-webkit-autofill {
    -webkit-text-fill-color: ${getColor('label')};
    box-shadow: 0 0 0px 1000px ${getColor('formFieldBackground')} inset;
    border: 16px solid ${getColor('formFieldBackground')};
    &:focus {
      box-shadow: 0 0 0px 1000px ${getColor('background')} inset;
      border: 16px solid ${getColor('background')};
      outline: 2px solid ${getColor('inputFocusBorder')};
    }
    &:disabled {
      -webkit-text-fill-color: ${getColor('disabledLabel')};
      box-shadow: 0 0 0px 1000px ${getColor('formFieldBackgroundDisabled')} inset;
      border: 16px solid ${getColor('formFieldBackgroundDisabled')};
    }

    &:first-line {
      ${props => props.theme.typography.inputText};
    }
  }
`

const Input = React.forwardRef((props: React.ComponentProps<typeof BaseInput>, ref: React.Ref<HTMLInputElement>) => {
  return <BaseInput aria-invalid={props.invalid ? true : undefined} ref={ref} {...props} />
})

export default Input
